<script setup>
import { ref, computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';

const props = defineProps({
    setting: Object,
    ulasans: Object, // Paginated data
});

const wa = computed(() => props.setting?.setting_no_wa || '6281234567890');
const waUrl = computed(() => `https://wa.me/${wa.value}?text=Halo,%20saya%20ingin%20sewa%20mobil`);

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

// Helper for stars
const getStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => i < rating);
};
</script>

<template>
    <FrontLayout 
        :setting="setting" 
        title="Ulasan Sewa Mobil Banyuwangi | Rental Mobil Banyuwangi" 
        metaDescription="Baca ulasan pelanggan sewa mobil Banyuwangi dan rental mobil Banyuwangi. Testimoni pengalaman nyata dari pengguna jasa Gotransport."
    >
        <Head>

            <meta name="description" content="Baca ulasan pelanggan sewa mobil Banyuwangi dan rental mobil Banyuwangi. Testimoni pengalaman nyata dari pengguna jasa Gotransport." />
        </Head>

        <!-- Hero Section -->
        <section class="relative overflow-hidden">
             <!-- Background with Orange Gradient (Matching Homepage) -->
             <div class="absolute inset-0 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-400"></div>
             
             <!-- Decorative Elements -->
             <div class="absolute inset-0 overflow-hidden pointer-events-none">
                <svg class="absolute right-0 top-0 text-white/10 w-64 h-64 -mr-16 -mt-16" fill="currentColor" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50"/></svg>
                <div class="absolute left-10 bottom-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
             </div>

             <div class="container mx-auto px-4 lg:px-6 xl:px-8 relative z-10 text-center py-12 md:py-16">
                 <!-- Breadcrumb (Moved to Top) -->
                 <div class="flex justify-center mb-6">
                     <div class="inline-flex items-center gap-2 text-xs md:text-sm text-white/90 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/20 shadow-sm">
                        <Link href="/" class="hover:text-white transition-colors font-medium">Beranda</Link>
                        <i class="fa fa-chevron-right text-[10px] opacity-70"></i>
                        <span class="text-white font-bold">Ulasan</span>
                     </div>
                 </div>

                 <h1 class="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight drop-shadow-sm">Ulasan Pelanggan</h1>
                 <p class="text-amber-100 text-lg max-w-2xl mx-auto font-medium">Testimoni asli dari pelanggan yang puas dengan layanan Gotransport</p>
             </div>
             
             <!-- Bottom Wave/Shape (Simple curve) -->
             <div class="absolute bottom-0 left-0 right-0 text-white dark:text-gray-900 transition-colors duration-300">
                 <svg viewBox="0 0 1440 48" fill="currentColor" class="w-full h-auto block">
                     <path d="M0,48 L1440,48 L1440,0 C1440,0 1140,48 720,48 C300,48 0,0 0,0 L0,48 Z"></path>
                 </svg>
             </div>
        </section>

        <!-- Main Content -->
        <section class="py-12 md:py-16 bg-white dark:bg-gray-900 min-h-[500px] transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                
                <div class="text-center mb-10">
                    <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 transition-colors">Ulasan Pelanggan Sewa & Rental Mobil Banyuwangi</h2>
                    <p class="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-4 transition-colors">
                        Testimoni pelanggan yang telah menggunakan layanan sewa mobil Banyuwangi dan rental mobil Banyuwangi dari Gotransport. 
                        Simak pengalaman nyata mereka.
                    </p>
                    <div class="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto"></div>
                </div>

                <!-- Google Reviews Widget (Elfsight) -->
                <div class="mb-12 bg-white rounded-xl p-4 md:p-6 shadow-sm border border-transparent">
                    <component :is="'script'" src="https://static.elfsight.com/platform/platform.js" data-use-service-core defer></component>
                    <div class="elfsight-app-d9239834-0e82-4389-99bf-1ccdeae96411" data-elfsight-app-lazy></div>
                </div>

                <!-- Local Reviews Removed as per request (using Google Reviews only) -->
            </div>
        </section>

        <!-- CTA Section -->
        <section class="py-12 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="max-w-3xl mx-auto text-center">
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 transition-colors">Ingin Berbagi Pengalaman Anda?</h3>
                    <p class="text-gray-600 dark:text-gray-300 mb-6 transition-colors">
                        Kami sangat menghargai feedback dari setiap pelanggan. 
                        Hubungi kami untuk berbagi pengalaman Anda menggunakan layanan Gotransport.
                    </p>
                    <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link href="/contact" 
                           class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold transition-colors rounded-full">
                            <i class="fa fa-envelope"></i>
                            <span>Hubungi Kami</span>
                        </Link>
                        <a v-if="setting?.setting_no_wa" :href="waUrl" 
                           target="_blank" rel="noopener" 
                           class="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold transition-colors rounded-full">
                            <i class="fa fa-whatsapp"></i>
                            <span>WhatsApp</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>

<style>
/* Override Elfsight Widget Styles for Button Visibility */
/* Target any elfsight app container to ensure specificity */
div[class*="elfsight-app"] .es-load-more-button {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
    border: 1px solid #d1d5db !important;
    opacity: 1 !important;
}
div[class*="elfsight-app"] .es-load-more-button:hover {
    background-color: #e5e7eb !important;
}
/* Ensure text inside the button is also dark */
div[class*="elfsight-app"] .es-load-more-button span {
    color: #111827 !important;
}
</style>
